import { useState } from 'react'

import { ObservableQuery } from '@apollo/client'
import { Card } from '@joor/design-system'
import { useSelector } from 'react-redux'

import { useRenderToasters } from 'hooks/useRenderToasters'
import { getDateFormat, isAccountTypeBrand } from 'store/currentUser/selectors'

import { OrderData } from '../../Overview'
import { LoadingOrderDetails } from './LoadingOrderDetails'
import {
  OrderDetailsEditForm,
  OrderDetailsViewList,
  WarehouseWarningModal,
} from './components'
import {
  useCanEditDetails,
  useOrderDetailsForm,
  useUpdateOrderDetails,
} from './orderDetailsWidget.hooks'
import { FormIds, updateDetailsUtils } from './orderDetailsWidget.utils'
import { ReviewPageIds } from 'features/Orders/Review/review.ids'
import { refreshOrderDetailsWidget } from 'features/Orders/Review/review.store'

export const OrderDetailsTestIds = {
  LoadingDetailsCard: 'loading-order-details-card',
  SeasonItem: 'order-details-season-item',
  YearItem: 'order-details-year-item',
  WarehouseItem: 'order-details-warehouse-item',
  DivisionCodeItem: 'order-details-division-code-item',
  OrderTypeItem: 'order-details-order-type-item',
  OrderDeliveryWindowItem: 'order-details-order-delivery-window-item',
  EventItem: 'order-details-event-item',
  EditActionButton: 'order-details-edit-button',
  EditForm: 'order-details-edit-form',
  WarehouseWarningModal: 'order-details-warehouse-warning-modal',
  OrderCreatedDateItem: 'order-details-created-date-item',
} as const

export type OrderDetailsQuery = {
  id: OrderData['id']
  brand?: OrderData['brand']
  status?: OrderData['status']
  collections?: OrderData['collections']
  deliveryWindow: OrderData['collections']['deliveryWindow']
  warehouses?: OrderData['warehouses']
  divisions?: OrderData['divisions']
  retailerProfile: OrderData['retailerProfile']
  types?: OrderData['types']
  created: OrderData['created']
  season: OrderData['season']
  event: OrderData['event']
}

type Props = {
  className?: string
  testId?: string
  details: OrderDetailsQuery
  loading?: boolean
  refetch: ObservableQuery['refetch']
}

export const OrderDetailsWidget = ({
  className = '',
  testId,
  details,
  loading,
  refetch,
}: Props) => {
  const isBrand = useSelector(isAccountTypeBrand)
  const dateFormat = useSelector(getDateFormat)

  const [showWarningModal, setShowWarningModal] = useState(false)

  const { renderToast } = useRenderToasters()

  const { canEditDetails, isEditing, setIsEditing } = useCanEditDetails(
    details?.status || undefined,
  )

  const { formValues, handleFormChange, resetForm } = useOrderDetailsForm(
    isEditing,
    details,
  )

  const { updateOrderDetails, mutationsLoading } = useUpdateOrderDetails()

  refreshOrderDetailsWidget(refetch)

  const handleEditBtnClick = () => {
    setIsEditing(true)
  }

  const handleSaveBtnClick = () => {
    if (
      formValues[FormIds.Warehouse]?.value !==
      details?.warehouses?.edges[0]?.node?.id
    ) {
      setShowWarningModal(true)
    } else {
      handleSubmit()
    }
  }

  const handleSubmit = async () => {
    const { updateOrderInfoPayload, updateOrderDeliveryInfoPayload } =
      updateDetailsUtils.createMutationsPayload({ formValues, details })

    const skipUpdateCall =
      Object.keys(updateOrderInfoPayload).length === 0 &&
      Object.keys(updateOrderDeliveryInfoPayload).length === 0

    if (skipUpdateCall) {
      setIsEditing(false)
      resetForm()
      return
    }

    const updateResults = await updateOrderDetails({
      updateOrderInfoPayload,
      updateOrderDeliveryInfoPayload,
    })

    const { status: type, title, message: description } = updateResults

    renderToast({
      type,
      description,
      title,
      ...(type !== 'success' && {
        action: {
          label: 'Try Again',
          handler: () => setIsEditing(true),
        },
      }),
      timeOut: 5000,
      shouldCloseManually: type !== 'success',
    })

    resetForm()
    setIsEditing(false)
  }

  if (loading || mutationsLoading) {
    return (
      <Card
        testId={OrderDetailsTestIds.LoadingDetailsCard}
        title="Order Details"
        className={className}
      >
        <LoadingOrderDetails fieldsAmount={isBrand ? 10 : 6} />
      </Card>
    )
  }

  const isRetailerWithRestriction =
    details?.brand?.orderSettings?.restrictEditNotesAndReturnedOrders &&
    !isBrand

  const isEditable = canEditDetails && !isRetailerWithRestriction

  return (
    <>
      <Card
        title="Order Details"
        testId={testId}
        disableActionButton={mutationsLoading}
        actionButtonId={ReviewPageIds.OrderDetailsEditButtonId}
        {...(isEditable && {
          actionButtonTestId: OrderDetailsTestIds.EditActionButton,
          actionButtonLabel: isEditing ? 'Save' : 'Edit',
          onClickActionButton: isEditing
            ? handleSaveBtnClick
            : handleEditBtnClick,
        })}
        className={className}
      >
        {isEditing ? (
          <OrderDetailsEditForm
            details={details}
            onFormChange={handleFormChange}
            values={formValues}
            dateFormat={dateFormat}
            testId={OrderDetailsTestIds.EditForm}
            isBrand={isBrand}
          />
        ) : (
          !loading && (
            <OrderDetailsViewList
              details={details}
              isBrand={isBrand}
              dateFormat={dateFormat}
            />
          )
        )}
      </Card>
      {showWarningModal && (
        <WarehouseWarningModal
          isOpen={showWarningModal}
          setOpen={setShowWarningModal}
          action={() => {
            setShowWarningModal(false)
            handleSubmit()
          }}
          testId={OrderDetailsTestIds.WarehouseWarningModal}
        />
      )}
    </>
  )
}
